







































































import { Component, Prop, Mixins, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import HasValidation from "@/mixins/HasValidation";
import Context from "@/mixins/context";
import RequestPromises from "@/mixins/requestPromises";
import CurrenciesStore from "@/mixins/constants/currenciesStore";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { UUID } from "@/data/enums";

// Types
import { IState } from "@/store";
import { IClient, IClientBillingBasicForm } from "@/models/clients";
import { IPricelist } from "@/models/products";
import { ICurrency } from "@/models/constants";
import { IBrand } from "@/models/brands";
import { IAccount } from "@/models/accounts";

@Component({
  computed: {
    ...mapState({
      brand: (state: IState) => state.brand,

      priceListsScope(state: IState, getters) {
        return getters[`data/${DataModules.PRICELISTS}/priceListsScope`]();
      },
      pricelists(state: IState, getters) {
        return (
          getters[`data/listArray`]({
            storeModule: DataModules.PRICELISTS,
            scope: this.priceListsScope
          }) || []
        );
      },

      brandSettingsScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/brandScope`](
          this.clientBrandId
        );
      },
      brandSettings(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.BRANDS,
          scope: this.brandSettingsScope
        });
      }
    })
  }
})
export default class ClientBillingBasicConfigurationForm extends Mixins(
  HasValidation,
  RequestPromises,
  Context,
  CurrenciesStore
) {
  // Map State

  public brand!: IBrand;
  public priceListsScope!: string;
  public pricelists!: IPricelist[];
  public brandSettingsScope!: string;
  public brandSettings!: IBrand;

  // Props

  @Prop({ required: true })
  public client!: IClient;

  @Prop({ default: false })
  public isSticky!: boolean;

  @Prop({ default: false })
  public isDisabled!: boolean;

  // Data

  public form = {} as IClientBillingBasicForm;

  public isLoading = true;
  public isReloading = false;
  public requestError = false;
  public isProcessing = false;

  // Life-cycle methods

  public async created() {
    this.loadData();
  }

  public beforeDestroy() {
    if (this.showPriceListsSelector)
      this.$store.dispatch("data/binList", {
        storeModule: DataModules.PRICELISTS,
        scope: this.priceListsScope,
        vm: this
      });

    if (this.isOrgMode)
      this.$store.dispatch("data/binSingle", {
        storeModule: DataModules.BRANDS,
        scope: this.brandSettingsScope,
        vm: this
      });
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getCurrencies(),
        this.getPriceLists(),
        this.getBrandSettings()
      ]);

      this.form = this.initForm();
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.requestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public getPriceLists() {
    if (!this.showPriceListsSelector) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.PRICELISTS}/list`, {
      scope: this.priceListsScope,
      params: {
        limit: 0,
        order: "user_id,name" // We order by user_id here as 'sys' should correspond to first/default pricelist
      },
      ignoreStored: this.isReloading,
      vm: this
    });
  }

  public async getBrandSettings() {
    if (!this.isOrgMode) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.BRANDS}/getBrandSettings`, {
      id: this.clientBrandId,
      scope: this.brandSettingsScope,
      ignoreStored: this.isReloading,
      vm: this
    });
  }

  public reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public initForm() {
    return {
      currency_id: this.currencyId,
      ...(this.showPriceListsSelector ? { pricelist_id: this.pricelistId } : {})
    };
  }

  public async validateBeforeSubmit() {
    try {
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");

      this.isProcessing = true;

      await Promise.all([
        this.updateClientCurrency(),
        this.updateClientPricelist()
      ]);

      this.$emit("refreshClient");

      if (!this.isSticky) {
        this.$store.dispatch("toast/show", {
          message: this.$t("_sentence.billing_basic_configuration_updated")
        });
      }
    } catch (error) {
      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public updateClientCurrency() {
    if (this.form.currency_id === this.currencyId) return Promise.resolve();

    return this.$store.dispatch(`data/${DataModules.CLIENTS_ACCOUNTS}/update`, {
      accountId: this.accountId,
      data: _.pick(this.form, "currency_id")
    });
  }

  public updateClientPricelist() {
    if (
      this.form.pricelist_id === this.pricelistId ||
      !this.showPriceListsSelector
    )
      return Promise.resolve();

    return this.$store.dispatch(
      `data/${DataModules.CLIENTS_ACCOUNTS}/assignPricelist`,
      {
        accountId: this.accountId,
        data: _.pick(this.form, "pricelist_id")
      }
    );
  }

  public revertToInitialState() {
    this.form = this.initForm();
  }

  // Computed

  public get clientAccount(): IAccount {
    return this.client.accounts[0];
  }

  public get clientId(): IClient["id"] {
    return this.client.id;
  }

  public get accountId(): IAccount["id"] {
    return this.clientAccount.id || "";
  }

  public get currencyId(): ICurrency["id"] {
    return this.clientAccount.currency_id || "";
  }

  public get pricelistId(): IPricelist["id"] {
    return this.clientAccount.pricelist_id || "";
  }

  public get clientBrandId(): IBrand["id"] {
    return this.client.brand_id || "";
  }

  public get isOrgMode() {
    return this.brand.id === UUID.ORG;
  }

  public get brandCurrencies() {
    return this.brandSettings?.currencies || this.brand.currencies;
  }

  public get defaultPriceListId() {
    return (this.brandSettings || this.brand).pricelist_id;
  }

  public get supportedCurrencies() {
    return _.orderBy(
      _.concat(
        this.brandCurrencies,
        !_.some(
          this.brandCurrencies,
          currency => currency.id === this.form.currency_id
        )
          ? _.filter(this.currencies, ["id", this.currencyId])
          : []
      ),
      "name",
      "asc"
    );
  }

  public get selectedPriceList() {
    return _.find(this.pricelists, ["id", this.form.pricelist_id]);
  }

  public get selectedPriceListName() {
    return this.selectedPriceList?.name;
  }

  public get isStaged() {
    return !!this.client?.staged_import;
  }

  public get formIsChanged() {
    return !this.$_.isEqual(this.form, this.initForm());
  }

  // Guarding

  public get canUpdate() {
    return this.$userCan("update_client");
  }

  public get showPriceListsSelector() {
    return this.isAdmin && this.$userCan("list_pricelists");
  }

  // Watchers

  @Watch("client")
  public onClientDataChange() {
    this.form = this.initForm();
  }

  @Watch("formIsChanged")
  public onIsEnabledChange() {
    this.$emit("isEnabled", this.formIsChanged);
  }

  @Watch("isProcessing")
  public onIsProcessingChange() {
    this.$emit("isProcessing", this.isProcessing);

    if (!this.isProcessing && !this.hasErrors) {
      this.$emit("isEnabled", false);
    }
  }
}
